/**
 * The title.
 *
 * Deliberately thin. It owns its buttons and no behaviour: CREATE SONG,
 * IMPORT SONGPACK and SETTINGS all open screens that already exist and are
 * already wired, so this hands the same handlers the Song Select bottom bar
 * hands. Nothing about creating, importing or configuring is implemented twice.
 *
 * It sits over the running world rather than replacing it — the mandala, the
 * starfield and the nebula keep turning behind it, which is what makes the
 * title feel like part of the game rather than a page in front of it.
 */

import { DEMO_TRACKS } from '../runtime/DemoRegistry.js';
import type { Difficulty } from '../songpack/index.js';
const STYLE_ID = 'mandalizm-title-style';

const CSS = `
.mandalizm-title {
  position: fixed; inset: 0; z-index: 45; display: grid; place-items: center;
  padding: 20px; box-sizing: border-box; overflow-y: auto;
  background: radial-gradient(circle at 50% 45%, rgba(2,20,26,0.62), rgba(0,2,8,0.9));
  font: 13px/1.7 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
  transition: opacity 420ms ease;
}
.mandalizm-title[hidden] { display: none; }
.mandalizm-title--leaving { opacity: 0; pointer-events: none; }
.mandalizm-title__inner { text-align: center; width: min(390px, 100%); min-width: 0; margin: auto; }
.mandalizm-title__name {
  font-size: clamp(24px, 7vw, 34px); letter-spacing: 0.24em; color: #eafeff; margin-bottom: 8px;
  text-shadow: 0 0 30px rgba(0, 220, 200, 0.45);
}
.mandalizm-title__tag {
  font-size: 10px; letter-spacing: 0.3em; color: rgba(140, 205, 220, 0.55);
  margin-bottom: 34px;
}
.mandalizm-title__menu { display: grid; gap: 9px; }
.mandalizm-demo-choice { display:grid; gap:6px; min-width:0; }
.mandalizm-title [hidden] { display:none; }
.mandalizm-title[data-view="landing"] .mandalizm-demo-choice,
.mandalizm-title[data-view="landing"] .mandalizm-demo-start { display:none; }
.mandalizm-title[data-view="demo"] .mandalizm-demo-entry { display:none; }
.mandalizm-demo-track { display:grid; grid-template-columns:44px minmax(0,1fr) 44px; gap:6px; align-items:center; }
.mandalizm-demo-track select { min-width:0; width:100%; text-overflow:ellipsis; }
.mandalizm-demo-choice button, .mandalizm-demo-choice select { font:inherit; padding:4px 6px; color:#cdf6f1; background:#06343d; border:1px solid #377b80; border-radius:6px; min-height:36px; }
.mandalizm-demo-difficulty { display:grid; grid-template-columns:repeat(3,1fr); gap:6px; }
.mandalizm-demo-difficulty [aria-pressed=true] { background:#126d72; border-color:#bff4ee; }
.mandalizm-demo-choice summary { cursor:pointer; font-size:11px; }
.mandalizm-demo-choice label { font-size:11px; }
.mandalizm-demo-count { font-size:10px; letter-spacing:.12em; }
.mandalizm-title__button {
  appearance: none; cursor: pointer; font: inherit; letter-spacing: 0.2em;
  padding: 13px 30px; color: #cdf6f1;
  background: rgba(4, 46, 54, 0.82); border: 1px solid rgba(90, 240, 220, 0.34);
  border-radius: 7px; transition: background 150ms ease, color 150ms ease, transform 150ms ease;
}
.mandalizm-title__button:hover:not(:disabled) {
  background: rgba(6, 92, 100, 0.95); color: #eafeff; transform: translateY(-1px);
}
.mandalizm-title__button--primary {
  color: #eafeff; border-color: rgba(120, 250, 232, 0.62); padding: 16px 30px;
  font-size: 15px; background: rgba(6, 83, 88, 0.92); box-shadow: 0 0 24px rgba(0, 220, 200, 0.12);
}
.mandalizm-title__button[hidden] { display: none; }
.mandalizm-title__button:focus-visible { outline: 2px solid #b9fff2; outline-offset: 3px; }
.mandalizm-title__button:disabled { opacity: 0.4; cursor: not-allowed; }
.mandalizm-title__status {
  margin-top: 22px; min-height: 1.6em; font-size: 11px; color: rgba(150, 215, 225, 0.7);
  overflow-wrap: anywhere;
}
@media (max-height: 540px) {
  .mandalizm-title { padding: 12px 20px; }
  .mandalizm-title__tag { margin-bottom: 14px; }
  .mandalizm-title__menu { gap: 6px; }
  .mandalizm-title__button { padding: 8px 20px; }
  .mandalizm-title__button--primary { padding: 10px 20px; }
  .mandalizm-title__status { margin-top: 10px; }
}
@media (max-height:540px) and (min-width:600px) {
  .mandalizm-title__inner { width:min(740px,100%); }
  .mandalizm-title__menu { grid-template-columns:minmax(0,1.25fr) minmax(0,1fr); gap:6px 14px; }
  .mandalizm-demo-choice { grid-row:span 5; align-content:start; }
  .mandalizm-title__name { font-size:24px; }
  .mandalizm-title__tag { margin-bottom:8px; }
  .mandalizm-title__status { margin-top:6px; }
}
`;

export interface TitleHandlers {
  readonly onDemoSpeed?: (speed: number) => void;
  readonly onDemoTrack?: (index: number) => void;
  readonly onDemoDifficulty?: (difficulty: Difficulty) => void;
  readonly onDemoArt?: (enabled: boolean) => void;
  readonly onDemo?: () => void | Promise<void>;
  readonly onPlay: () => void;
  readonly onCreate: () => void;
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onSettings: () => void;
}

export class TitleScreen {
  private readonly trackSelect = document.createElement('select');
  private readonly trackCount = document.createElement('div');
  private readonly difficultyButtons: HTMLButtonElement[] = [];
  private readonly speedButtons: HTMLButtonElement[] = [];
  readonly element: HTMLDivElement;

  private readonly playButton: HTMLButtonElement;
  private readonly demoButton: HTMLButtonElement | null;
  private readonly uploadButtons: HTMLButtonElement[] = [];
  private readonly buttons: HTMLButtonElement[] = [];
  private readonly importInput: HTMLInputElement;
  private readonly status: HTMLDivElement;
  private playable = true;
  private demoReady = true;
  private busy = false;

  constructor(handlers: TitleHandlers, parent: HTMLElement = document.body) {
    injectStyle();
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-title';
    this.element.dataset.view = 'landing';
    this.element.hidden = true;

    const inner = document.createElement('div');
    inner.className = 'mandalizm-title__inner';

    const name = document.createElement('div');
    name.className = 'mandalizm-title__name';
    name.textContent = 'MANDALIZM';
    const tag = document.createElement('div');
    tag.className = 'mandalizm-title__tag';
    tag.textContent = 'RHYTHM · GEOMETRY · RESONANCE';

    const menu = document.createElement('div');
    menu.className = 'mandalizm-title__menu';

    this.demoButton = handlers.onDemo === undefined ? null : button('PLAY DEMO', true);
    if (this.demoButton !== null) {
      const entry = button('DEMO', true);
      entry.classList.add('mandalizm-demo-entry');
      entry.addEventListener('click', () => this.setDemoView(true));
      menu.append(entry);
      this.demoButton.classList.add('mandalizm-demo-start');
      const choices = document.createElement('div');
      choices.className = 'mandalizm-demo-choice';
      this.trackCount.className = 'mandalizm-demo-count';
      const trackRow = document.createElement('div');
      trackRow.className = 'mandalizm-demo-track';
      this.trackSelect.setAttribute('aria-label', 'Demo track');
      DEMO_TRACKS.forEach((track, index) => this.trackSelect.add(new Option(track.title, String(index))));
      this.trackSelect.addEventListener('change', () => handlers.onDemoTrack?.(this.trackSelect.selectedIndex));
      const previous = button('‹'); previous.setAttribute('aria-label', 'Previous demo track');
      const next = button('›'); next.setAttribute('aria-label', 'Next demo track');
      previous.addEventListener('click', () => handlers.onDemoTrack?.((this.trackSelect.selectedIndex + 4) % 5));
      next.addEventListener('click', () => handlers.onDemoTrack?.((this.trackSelect.selectedIndex + 1) % 5));
      trackRow.append(previous, this.trackSelect, next);
      const difficulties = document.createElement('div');
      difficulties.className = 'mandalizm-demo-difficulty';
      for (const difficulty of ['easy', 'normal', 'hard'] as const) {
        const control = button(difficulty.toUpperCase());
        control.dataset.difficulty = difficulty;
        control.addEventListener('click', () => handlers.onDemoDifficulty?.(difficulty));
        this.difficultyButtons.push(control); difficulties.append(control);
      }
      const advanced = document.createElement('details');
      const speedLabel = document.createElement('div'); speedLabel.textContent = 'SPEED';
      const speeds = document.createElement('div'); speeds.className = 'mandalizm-demo-difficulty';
      for (const speed of [0.75, 1, 1.25]) {
        const control = button(`${speed}x`);
        control.dataset.speed = String(speed);
        control.addEventListener('click', () => handlers.onDemoSpeed?.(speed));
        this.speedButtons.push(control); speeds.append(control);
      }
      this.setDemoSpeed(1);
      const difficultyLabel = document.createElement('div'); difficultyLabel.textContent = 'DIFFICULTY';
      const summary = document.createElement('summary'); summary.textContent = 'ADVANCED';
      const label = document.createElement('label');
      const art = document.createElement('input'); art.type = 'checkbox'; art.setAttribute('aria-label', 'Demo Art Mode');
      art.addEventListener('change', () => handlers.onDemoArt?.(art.checked));
      label.append(art, ' ART MODE'); advanced.append(summary, label);
      choices.append(this.trackCount, trackRow, difficultyLabel, difficulties, speedLabel, speeds, advanced);
      this.buttons.push(previous, next, ...this.difficultyButtons, ...this.speedButtons);
      menu.append(choices);
      this.setDemoSelection(0, 'normal');
      this.demoButton.addEventListener('click', () => void handlers.onDemo?.());
      this.buttons.push(this.demoButton);
      menu.appendChild(this.demoButton);
    }
    this.playButton = button(handlers.onDemo === undefined ? 'PLAY' : 'CUSTOM PLAY', handlers.onDemo === undefined);
    this.playButton.addEventListener('click', () => handlers.onPlay());
    const create = button('CREATE SONG');
    create.addEventListener('click', () => handlers.onCreate());
    const importButton = button('IMPORT SONGPACK');
    this.importInput = document.createElement('input');
    this.importInput.type = 'file';
    this.importInput.accept = '.zip,application/zip';
    this.importInput.hidden = true;
    importButton.addEventListener('click', () => this.importInput.click());
    this.importInput.addEventListener('change', () => {
      const file = this.importInput.files?.[0];
      // Cleared first, so choosing the same file twice fires again.
      this.importInput.value = '';
      if (file !== undefined) void handlers.onImport(file);
    });
    const settings = button('SETTINGS');
    settings.addEventListener('click', () => handlers.onSettings());

    this.buttons.push(this.playButton, create, importButton, settings);
    this.uploadButtons.push(create, importButton);
    menu.append(this.playButton, create, importButton, settings, this.importInput);

    this.status = document.createElement('div');
    this.status.className = 'mandalizm-title__status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');

    inner.append(name, tag, menu, this.status);
    this.element.appendChild(inner);
    parent.appendChild(this.element);
  }

  get visible(): boolean {
    return !this.element.hidden;
  }

  show(): void {
    this.element.hidden = false;
    this.element.classList.remove('mandalizm-title--leaving');
  }

  hide(): void {
    this.element.classList.add('mandalizm-title--leaving');
    window.setTimeout(() => {
      // Re-checked: the player may have come back before the fade finished.
      if (this.element.classList.contains('mandalizm-title--leaving')) {
        this.element.hidden = true;
      }
    }, 440);
  }

  /** Disables PLAY when the library is empty — there is nothing to play. */
  setPlayable(playable: boolean): void {
    this.playable = playable;
    this.updateButtons();
  }

  setDemoView(demo: boolean): void {
    this.element.dataset.view = demo ? 'demo' : 'landing';
    for (const details of this.element.querySelectorAll('details')) details.open = false;
  }

  setDemoSpeed(speed: number): void {
    for (const control of this.speedButtons) control.setAttribute('aria-pressed', String(Number(control.dataset.speed) === speed));
  }

  /** Demo readiness is independent of the user's song library. */
  setDemoReady(ready: boolean): void {
    this.demoReady = ready;
    this.updateButtons();
  }

  setDemoSelection(index: number, difficulty: Difficulty): void {
    this.trackSelect.selectedIndex = index;
    this.trackSelect.title = DEMO_TRACKS[index]!.title;
    this.trackCount.textContent = `DEMO TRACK ${index + 1} / ${DEMO_TRACKS.length}`;
    for (const control of this.difficultyButtons) control.setAttribute('aria-pressed', String(control.dataset.difficulty === difficulty));
  }

  /** A bundled host may omit features that require local uploads or a bridge. */
  setPlayables(playables: boolean): void {
    for (const control of this.uploadButtons) control.hidden = playables;
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    this.updateButtons();
  }

  private updateButtons(): void {
    this.trackSelect.disabled = this.busy;
    for (const control of this.buttons) control.disabled = this.busy;
    this.playButton.disabled = this.busy || !this.playable;
    if (this.demoButton !== null) this.demoButton.disabled = this.busy || !this.demoReady;
  }

  setStatus(text: string): void {
    this.status.textContent = text;
  }

  dispose(): void {
    this.element.remove();
  }
}

function button(text: string, primary = false): HTMLButtonElement {
  const element = document.createElement('button');
  element.className = primary
    ? 'mandalizm-title__button mandalizm-title__button--primary'
    : 'mandalizm-title__button';
  element.type = 'button';
  element.textContent = text;
  return element;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
